/**
 * 날짜 칸 — 적어도 되고, 달력에서 골라도 된다(2026-11-03, 무재해 현황의 시작일부터).
 *
 * `<input type="date">` 는 브라우저·로캘마다 모양이 달라("2026. 11. 03.") 좁은 칸에서는 일이 잘려 년·월만 보였다. 이 칸은
 * 늘 `YYYY-MM-DD` 열 글자를 다 보인다. 값도 브라우저 날짜 칸과 같은 `YYYY-MM-DD`(비우면 빈 글자)다.
 *
 * - 칸이나 달력 단추를 누르면 아래에 그달 달력이 열린다. 날을 고르면 닫힌다. ‹ › 로 달을 옮기고 [오늘] 로 오늘을 고른다.
 * - 적어도 된다 — "20261103"·"2026.11.3"·"11/3" 을 칸을 떠날 때·Enter 에 읽는다(`parseDateText`). 못 읽으면 앞의 값으로
 *   돌아간다. ↑·↓ 는 하루씩. 비워 둘 수 있는 칸(`clearable`)은 지우면 빈 값이고 달력에 [지우기] 가 있다.
 * - 달력은 문서 끝에 띄운다(시각 칸·⋯ 메뉴와 같다) — 패널의 `overflow: hidden` 에 잘리지 않게. 필터 창 안에서 열어도 그 창이
 *   닫히지 않게 `data-float-layer` 를 단다(`FilterPopover`).
 * - 초점은 칸에 남는다. 달력 단추는 Tab 으로 가지 않고, 창 안에서 Esc 는 달력만 닫는다.
 */
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { placeMenu } from './MoreMenu.js';
import { TimeField } from './TimeField.js';
import { localDate } from './localDate.js';
import { isDateText, monthGrid, parseDateText, shiftMonth, stepDate, WEEKDAY_LABELS } from './dateText.js';

export function DateField({
  value,
  onChange,
  label,
  clearable = false,
  disabled = false,
  required = false,
  wide = false,
}: {
  /** `YYYY-MM-DD` — 비어 있으면 빈 글자. */
  value: string;
  onChange: (next: string) => void;
  /** 화면 낭독기와 달력 머리에 쓰는 이름 — "시작일". */
  label: string;
  /** 비워 둘 수 있는 칸 — 글자를 지우면 빈 값, 달력에 [지우기]. */
  clearable?: boolean;
  disabled?: boolean;
  /** 양식(form)이 비었을 때 막게. */
  required?: boolean;
  /** 담긴 칸의 폭을 다 쓴다([화면 편집] 의 오른쪽 칸). */
  wide?: boolean;
}): ReactNode {
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  /** 적는 중인 글자 — 칸을 떠나거나 Enter 를 누를 때 읽는다. */
  const [draft, setDraft] = useState<string | null>(null);
  const today = localDate(new Date());
  const shown = isDateText(value) ? value : today;
  /** 달력이 보이는 달 — 열 때 지금 값(없으면 오늘)의 달로. */
  const [view, setView] = useState(() => ({ year: Number(shown.slice(0, 4)), month: Number(shown.slice(5, 7)) }));

  const close = useCallback(() => {
    setOpen(false);
    setPosition(null);
  }, []);

  const openPicker = (): void => {
    if (disabled || open) return;
    setView({ year: Number(shown.slice(0, 4)), month: Number(shown.slice(5, 7)) });
    setOpen(true);
  };

  const pick = (next: string, done: boolean): void => {
    setDraft(null);
    if (next !== value) onChange(next);
    if (done) close();
  };

  const commit = (): void => {
    if (draft === null) return;
    if (!draft.trim() && clearable) {
      pick('', false);
      return;
    }
    const parsed = parseDateText(draft, today);
    setDraft(null);
    if (parsed && parsed !== value) onChange(parsed);
  };

  useLayoutEffect(() => {
    if (!open || !wrap.current || !box.current) return;
    const a = wrap.current.getBoundingClientRect();
    const b = box.current.getBoundingClientRect();
    // 칸의 왼쪽 끝에 맞춘다 — placeMenu 는 오른쪽 끝에 맞추므로 달력 폭만큼 넘겨 준다.
    const next = placeMenu(
      { top: a.top, bottom: a.bottom, left: a.left, right: a.left + b.width },
      { width: b.width, height: b.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPosition((current) => (current && current.top === next.top && current.left === next.left ? current : next));
  });

  useEffect(() => {
    if (!open) return;
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node && (wrap.current?.contains(target) === true || box.current?.contains(target) === true);
    const onPointer = (event: PointerEvent): void => {
      if (!inside(event.target)) close();
    };
    const onScroll = (event: Event): void => {
      if (!inside(event.target)) close();
    };
    document.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  return (
    <div ref={wrap} className={`date-field${wide ? ' date-field--wide' : ''}`}>
      <input
        ref={input}
        className="input input-date"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="YYYY-MM-DD"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-haspopup="dialog"
        disabled={disabled}
        required={required}
        value={draft ?? value}
        onChange={(event) => setDraft(event.target.value)}
        onClick={openPicker}
        onBlur={() => {
          // 달력·단추를 누를 때는 초점이 떠나지 않는다(누름을 막아 둔다) — 여기 오는 것은 정말 떠난 것이다.
          commit();
          close();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
            close();
          } else if (event.key === 'Escape' && open) {
            // 달력만 닫는다 — 창(Modal)·필터 창이 이 키로 닫히지 않게(`defaultPrevented` 를 본다).
            event.preventDefault();
            setDraft(null);
            close();
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            const base = (draft !== null ? parseDateText(draft, today) : null) ?? (isDateText(value) ? value : today);
            const next = stepDate(base, event.key === 'ArrowUp' ? 1 : -1);
            setView({ year: Number(next.slice(0, 4)), month: Number(next.slice(5, 7)) });
            pick(next, false);
          }
        }}
      />
      <button
        type="button"
        className="date-field__toggle"
        tabIndex={-1}
        disabled={disabled}
        aria-label={`${label} 고르기`}
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) close();
          else {
            openPicker();
            input.current?.focus();
          }
        }}
      >
        <CalendarDays className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={box}
              id={id}
              role="dialog"
              aria-label={`${label} 달력`}
              className="date-pop"
              data-float-layer=""
              style={position ?? { top: -9999, left: -9999 }}
              // 초점은 칸에 남긴다 — 달력을 눌러도 칸이 blur 되어 적던 글자를 먼저 읽지 않게.
              onPointerDown={(event) => event.preventDefault()}
            >
              <div className="date-pop__head">
                <button
                  type="button"
                  tabIndex={-1}
                  className="date-pop__nav"
                  aria-label="지난달"
                  onClick={() => setView((current) => shiftMonth(current, -1))}
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                </button>
                <span className="date-pop__title">
                  {view.year}년 {view.month}월
                </span>
                <button
                  type="button"
                  tabIndex={-1}
                  className="date-pop__nav"
                  aria-label="다음 달"
                  onClick={() => setView((current) => shiftMonth(current, 1))}
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
              <div className="date-pop__grid" role="grid">
                {WEEKDAY_LABELS.map((name, index) => (
                  <span key={name} className={`date-pop__weekday${index === 0 ? ' is-sun' : ''}`} role="columnheader">
                    {name}
                  </span>
                ))}
                {monthGrid(view.year, view.month).map((cell, index) => (
                  <button
                    key={cell.date}
                    type="button"
                    tabIndex={-1}
                    role="gridcell"
                    aria-selected={cell.date === value}
                    aria-label={cell.date}
                    className={[
                      'date-pop__day',
                      cell.inMonth ? '' : 'is-outside',
                      cell.date === today ? 'is-today' : '',
                      index % 7 === 0 ? 'is-sun' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => pick(cell.date, true)}
                  >
                    {cell.day}
                  </button>
                ))}
              </div>
              <div className="date-pop__foot">
                <button type="button" tabIndex={-1} className="btn btn-tiny" onClick={() => pick(today, true)}>
                  오늘
                </button>
                {clearable && value ? (
                  <button type="button" tabIndex={-1} className="btn btn-tiny" onClick={() => pick('', true)}>
                    지우기
                  </button>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * 날짜와 시각 칸 — `YYYY-MM-DDTHH:MM`(브라우저 `datetime-local` 과 같은 값, 비우면 빈 글자). 날짜는 `DateField`, 시각은
 * `TimeField`(24시간). 날짜를 처음 고르면 시각은 `defaultTime` 으로 채운다 — 끝을 정하는 칸은 그날 끝(23:59)이 자연스럽다.
 * 날짜를 지우면 값 전체가 빈다. 날짜가 없으면 시각 칸은 쉰다.
 */
export function DateTimeField({
  value,
  onChange,
  label,
  defaultTime = '00:00',
  clearable = true,
  disabled = false,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  defaultTime?: string;
  clearable?: boolean;
  disabled?: boolean;
}): ReactNode {
  const [date = '', rest = ''] = value ? value.split('T') : [];
  const time = rest.slice(0, 5);
  return (
    <div className="date-time-field">
      <DateField
        value={date}
        label={`${label} 날짜`}
        clearable={clearable}
        disabled={disabled}
        onChange={(next) => onChange(next ? `${next}T${time || defaultTime}` : '')}
      />
      <TimeField
        value={time}
        label={`${label} 시각`}
        disabled={disabled || !date}
        onChange={(next) => {
          if (date) onChange(`${date}T${next}`);
        }}
      />
    </div>
  );
}
